import { useMemo } from 'react';
import { isAfter } from 'date-fns';

export function useLinkedIncidents(excelData: { [key: string]: any[] }) {
  return useMemo(() => {
    const catalogTasks = excelData['Catalog Tasks'] || [];
    const incidents = excelData['Incidents'] || [];
    const linked = [];

    // Helper function to extract API name
    const extractApiName = (description: string): string | null => {
      const apiRegex = /\b([a-z0-9-]+[-]?api)\b/i;
      const apiMatch = description.match(apiRegex);
      
      if (apiMatch) return apiMatch[0];
      
      const systemRegex = /\b([a-z0-9-]+[-](?:sys|prc|exp))\b/i;
      const systemMatch = description.match(systemRegex);
      
      return systemMatch ? systemMatch[0] : null;
    };

    // Helper function to check for task keywords in incident description
    const containsTaskKeywords = (incidentDesc: string, taskKeywords: string[]): boolean => {
      return taskKeywords.some(keyword => 
        incidentDesc.toLowerCase().includes(keyword)
      );
    };

    for (const task of catalogTasks) {
      const taskDescription = task.short_description || task.description || "";
      const apiName = extractApiName(taskDescription) || "Unknown System";
      
      // --- Add capture of the catalog task's opened date
      const taskDateRaw = task.opened || task.created || task.opened_at || task.created_at || "";
      const taskDate = new Date(taskDateRaw);
      if (isNaN(taskDate.getTime())) continue;
      
      const taskKeywords = taskDescription
        .toLowerCase()
        .split(/\s+/)
        .filter(word => word.length >= 4 && !word.match(/^\d+$/))
        .slice(0, 3);
      
      for (const incident of incidents) {
        const incidentDesc = incident.short_description || incident.description || "";
        const incidentDate = new Date(incident.opened || incident.created || incident.opened_at || incident.created_at || "");
        
        if (isNaN(incidentDate.getTime())) continue;
        
        const containsApiName = apiName !== "Unknown System" && 
          incidentDesc.toLowerCase().includes(apiName.toLowerCase());
        
        if (isAfter(incidentDate, taskDate) && (containsApiName || containsTaskKeywords(incidentDesc, taskKeywords))) {
          linked.push({
            task_number: task.number || task.task_number,
            api_name: apiName,
            incident_number: incident.number || incident.incident_number,
            incident_description: incidentDesc,
            incident_created_date: incident.opened || incident.created || incident.opened_at || incident.created_at,
            priority: incident.priority,
            status: incident.status || incident.state,
            catalog_task_date: taskDateRaw // <-- Pass the raw date string for display
          });
        }
      }
    }

    return linked;
  }, [excelData]);
}
